
import React from 'react';
import { connect } from 'react-redux';
import cx from 'classnames';
import moment from 'moment';
import { createSelector } from 'reselect';


import { getTwitchAuthURL, openAuthWindow } from 'utils/twitch';
import Overlay, { selectHealthCheck, selectOverlay } from 'store/models/Overlay';
import Account, { selectCurrentAccount } from 'store/models/Account';
import { selectLiveSession } from 'store/models/Session';
import Channel, { selectChannel } from 'store/models/Channel';

import Modal, { ModalHeader, ModalSubtitle, ModalContent, ModalLoading, ModalActions, ModalActionHelper, ModalTitle } from 'components/Modal';
import Button, { TwitchButton } from 'components/Button';
import SwitchInput from 'components/forms/SwitchInput';
import { CheckmarkIcon, XMarkIcon } from 'components/Svg';
import * as Icons from 'components/Svg';
import ChannelCard from 'components/ChannelCard';
import { didPropChange } from '../utils/props';



// ======================
// STORE
// ====================== 
const actions = {
  fetchData: (overlayId, channelId) => dispatch => {
    return dispatch(Overlay.actions.getHealthCheck(overlayId, channelId));
  },
  updateChannelSettings: Channel.actions.updateChannelSettings,
}

const selectors = createSelector(
  selectCurrentAccount,
  (state, props) => selectHealthCheck(state, props.overlayId, props.channelId),
  (state, props) => selectOverlay(state, props.overlayId),
  (state, props) => selectChannel(state, props.channelId),
  (state, props) => !_.isEmpty(selectLiveSession(state, props.overlayId, props.channelId)),
  (account, health, overlay, channel, isLive) => Object.assign({ 
    health, 
    overlay, 
    channel, 
    channelSettings: account?.channels
      |> _.find(#, { channelId: channel?._id }) 
      |> #?.settings,
    isLive: health?.isLive })
)

const withConnect = connect(selectors, actions);



// ======================
// PARTIALS
// ====================== 

const StatusItem = ({label, status, disabled, className, ...props}) => (
  <div className={cx('status-item', className, { disabled })}>
    <div className="status">{status}</div>
    <div className="label">{label}</div>
  </div>
)

const StatusItemChecked = ({ status, ...props }) => (<StatusItem status={<Icons.CheckmarkIcon size="l" color="green" />} {...props} />)
const StatusItemUnchecked = ({ status, ...props }) => (<StatusItem status={<Icons.XMarkIcon size="l" color="red" />} className="warning" {...props} />)

const RunningStatus = ({ isLive, status, ...props }) => {
  let label;
  if(isLive) {
    label = status
      ? "Running in this stream"
      : "Not running in this stream"
  } else {
    label = status
      ? "Ran with Slideshow"
      : "Did not run with Slideshow"
  }
  if(status) {
    return <StatusItemChecked label={label} status={status} {...props} />;
  } else {
    return <StatusItemUnchecked label={label} status={status} {...props} />;
  }
}

const ObsStatus = ({ isLive, status, ...props }) => {
  let label;
  if(isLive) {
    label = status 
      ? "Running in OBS"
      : "Not Running in OBS"
  } else {
    label = status
      ? "Ran in OBS"
      : "Did not run in OBS"
  }
  if(status) {
    return <StatusItemChecked label={label} {...props} />;
  } else {
    return <StatusItemUnchecked label={label} {...props} />;
  }
}

const VerifiedStatus = ({ isLive, status, ...props }) => {
  return null;
  let label;
  if(isLive) {
    label = status
      ? "Verified OBS Settings"
      : "OBS settings may not be correct"
  } else {
    label = status
      ? "Verified OBS Settings"
      : "OBS settings may not have been correct"
  }
  if(status) {
    return <StatusItemChecked label={label} {...props} />;
  } else {
    return <StatusItemUnchecked label={label} {...props} />;
  }
}



// ======================
// COMPONENT
// ====================== 
class HealthCheckModal extends React.Component {
  state = {
    loading: false,
    disableSecurity: false,
  }

  componentDidMount = async () => {
    this.setState({ loading: true });
    await this.props.fetchData(this.props.overlayId, this.props.channelId);
    this.setState({ loading: false });

    if(this.props.channelSettings)
      this.setState({ disableSecurity: this.props.channelSettings.disableObsCheck });
  }

  componentDidUpdate() {
    if(didPropChange('channelSettings'))
      this.setState({ disableSecurity: this.props.channelSettings.disableObsCheck });
  }

  onSecurityChange = value => this.setState({ disableSecurity: !value })

  onDoneClick = async () => {
    let { channel, channelSettings, updateChannelSettings, closeModal } = this.props;

    if(this.state.disableSecurity === channelSettings?.disableObsCheck)
      return closeModal?.()  

    await updateChannelSettings(
      channel?._id, 
      { disableObsCheck: this.state.disableSecurity });
    closeModal?.();
  }

  render() {
    let { health, channel } = this.props;
    let { loading, disableSecurity } = this.state;
    let { isObs, verifiedObs, hasStreamed, lastStreamDate, lastStreamHadSession, isLive, isRunning } = health ?? {};
    console.log({
      health,
      disableSecurity
    })
    return (
      <Modal className="health-check">
        <ModalHeader>
          <ChannelCard channel={channel} layout="hor" size="l" />
          <ModalTitle>Slideshow Health Status:</ModalTitle>
        </ModalHeader>
        { loading && 
        <ModalLoading />}
        { !loading && 
        <ModalContent>
          { !hasStreamed && 
          <div className="not-streamed">
            This channel hasn't streamed yet. Return here after their first stream to ensure the slideshow is installed correctly
          </div>}
          { hasStreamed && isLive &&
            <div className="status-list last-session">
              <RunningStatus isLive={isLive} status={isRunning} />
              { !disableSecurity && <>  
              <ObsStatus isLive={isLive} status={isObs} disabled={disableSecurity} />
              <VerifiedStatus isLive={isLive} status={verifiedObs} disabled={disableSecurity} />
              </>}
            </div>}
          { hasStreamed && !isLive && <>
            <div className="last-stream">From Last Stream: { moment(lastStreamDate).format("MM/DD/YY hh:ssa") }</div>
            <div className="status-list last-session">
              <RunningStatus isLive={isLive} status={lastStreamHadSession} />
              {!disableSecurity && <>
              <ObsStatus isLive={isLive} status={isObs} />
              <VerifiedStatus isLive={isLive} status={verifiedObs} />
              </>}
            </div>
          </>}
          { ((isLive && isRunning) || (!isLive && lastStreamHadSession)) &&
          <div className="health-success">Success! Everything looks good!</div>}
          <div className="switch">
            
          </div>
        </ModalContent>}
        <ModalActions>
          <Button onClick={this.onDoneClick}>Done</Button>
        </ModalActions>
      </Modal>);
  }
};
export default HealthCheckModal
  |> withConnect
